import { Controller } from '@hotwired/stimulus'

export interface AttachEvent extends Event {
  detail: {
    files: FileList
  }

  accepts: {
    any: boolean
    mimes: string[]
    extensions: string[]
    globs: string[]
  }
}

export default class DropzoneController extends Controller<HTMLElement> {
  declare inputTarget: HTMLInputElement

  static targets = ['input']

  inputTargetConnected(el: HTMLInputElement) {
    const acceptables = el.accept.toLowerCase().split(/,\s*/).filter(Boolean)
    this.accepts = {}
    const any = el.accept.length === 0
    const mimes = acceptables.filter(str => /\/[^*]/.test(str))
    const extensions = acceptables.filter(str => /\./.test(str))
    const globs = acceptables.filter(str => /\/\*/.test(str)).map(glob => glob.split('/')[0])
    this.accepts = {
      any,
      mimes,
      extensions,
      globs,
    }
  }

  connect() {
    this.element.addEventListener('dragenter', this.#handleDragEnter.bind(this))
    this.element.addEventListener('dragleave', this.#handleDragLeave.bind(this))
    this.element.addEventListener('dragover', this.#handleDragOver.bind(this))
    this.element.addEventListener('drop', this.#handleDrop.bind(this))
    this.inputTarget.addEventListener('change', this.#handleInput.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('dragenter', this.#handleDragEnter.bind(this))
    this.element.removeEventListener('dragleave', this.#handleDragLeave.bind(this))
    this.element.removeEventListener('dragover', this.#handleDragOver.bind(this))
    this.element.removeEventListener('drop', this.#handleDrop.bind(this))
    this.inputTarget.removeEventListener('change', this.#handleInput.bind(this))
  }

  // Public methods

  cancelDrag = () => {
    this.element.classList.remove('dragover', 'dragover-invalid')
  }

  // Private methods

  #attachFiles(files: FileList) {
    this.dispatch('attach', { detail: { files } })
    this.inputTarget.value = ''
  }

  #handleDragEnter(event: DragEvent) {
    if (!event.dataTransfer) return

    const items = Array.from(event.dataTransfer.items)
    const types = event.dataTransfer.types
    const files = items.filter(item => item.kind === 'file')
    const noFiles = files.length === 0
    const tooManyFiles = !this.inputTarget.multiple && files.length > 1
    /* We can't check if the filename is valid here, but we can check mime-types
     * If the mime is invalid, and the accept property doesn't contain any
     * extensions then the files are invalid. If extensions are specified then
     * we can't be certain if the files are invalid or not. So we assume they're
     * OK, until the actual drop.
     */
    const invalidFiles = this.#getUnacceptableByType(files, types).length && this.accepts.extensions.length === 0

    if (noFiles || tooManyFiles || invalidFiles) {
      this.element.classList.add('dragover-invalid')
    } else {
      this.element.classList.add('dragover')
    }
  }

  #handleDragLeave(event: DragEvent) {
    const draggingOnZoneElem = event.relatedTarget === this.element
    const draggingInZoneElem = this.element.contains(event.relatedTarget as Node)

    if (draggingOnZoneElem || draggingInZoneElem) return

    this.element.classList.remove('dragover', 'dragover-invalid')
  }

  #handleDragOver(event: DragEvent) {
    event.preventDefault() // Required to allow drop
  }

  #handleDrop(event: DragEvent) {
    event.preventDefault() // Required to handle drop

    if (!event.dataTransfer) return

    this.element.classList.remove('dragover')

    const { files, types } = event.dataTransfer
    const hasFiles = files?.length > 0
    const hasTooManyFiles = !this.inputTarget.multiple && files?.length > 1
    const hasValidFiles = this.#hasValidFiles(files, types)

    if (hasFiles && !hasTooManyFiles && hasValidFiles) this.#attachFiles(files)
  }

  #handleInput(event: Event) {
    const files = (event.target as HTMLInputElement).files

    if (files && files.length > 0 && event.target.checkValidity()) this.#attachFiles(files)
  }

  #hasValidFiles(files: FileList, types: string[]): boolean {
    const invalidTypedFiles = this.#getUnacceptableByType(files, types)
    return this.#getUnacceptableByExtension(invalidTypedFiles).length === 0
  }

  #getUnacceptableByType(files: FileList, types: string[]): file[] {
    // We'll take anything
    if (this.accepts.any) return []

    // We specified specific mime types, and they're here. No need to figure out globs or file extensions
    if (types?.every(type => this.accepts.mimes.includes(type))) {
      return []
    }

    // Guess we're gonna do this the hard way
    const unacceptedFiles = Array.from(files)
      .filter(file => file.type.length)
      .filter(file => !this.accepts.mimes.includes(file.type))
      .filter(file => {
        const category = file.type.split(/\//)[0]
        return !this.accepts.globs.includes(category)
      })
    return unacceptedFiles
  }

  #getUnacceptableByExtension(files: FileList | file[]): file[] {
    // We'll take anything
    if (this.accepts.any) return []

    const nameMatchesExtension = (extension: string, filename: string): boolean =>
      new RegExp(`\\${extension}$`).test(filename)

    const unacceptedFiles = Array.from(files).filter(file => {
      return !this.accepts.extensions.some(extension => {
        const fileName = file.name
        if (!fileName) return false
        return nameMatchesExtension(extension, fileName)
      })
    })
    return unacceptedFiles
  }
}
